/**
 * Artifact catalog for script-audio-align lesson visuals.
 * Mirrors backend/script_audio_align/artifacts.py (id, label, category, kind).
 * Keep in sync when adding/removing artifact types on the backend.
 */

export const RENDER_KINDS = {
  TITLE: 'title',
  PROSE: 'prose',
  LIST: 'list',
  STEPS: 'steps',
  MERMAID: 'mermaid',
  TREE: 'tree',
  TABLE: 'table',
  CODE: 'code',
  TIMELINE: 'timeline',
  CHART: 'chart',
  EXAMPLE: 'example',
  QUIZ: 'quiz',
  MAP: 'map',
  IMAGE: 'image',
  FAQ: 'faq',
  CALLOUT: 'callout',
  STATE: 'state',
  ORIGIN: 'origin',
};

export const CATEGORY_LABELS = {
  foundation: 'Foundation',
  structure: 'Structure',
  process: 'Process',
  temporal: 'Temporal',
  spatial: 'Spatial',
  data: 'Data',
  comparison: 'Comparison',
  practical: 'Practical',
  code: 'Code',
  math: 'Math',
  reasoning: 'Reasoning',
  real_world: 'Real world',
  interactive: 'Interactive',
  guidance: 'Guidance',
  systems: 'Systems',
  reinforcement: 'Reinforcement',
  internal: 'Internal',
};

export const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

const K = RENDER_KINDS;

/** [id, label, category, render_kind, legacy?] */
const DEFS = [
  // Foundation
  ['explanation', 'Explanation', 'foundation', K.PROSE],
  ['definition', 'Definition', 'foundation', K.PROSE],
  ['concept_breakdown', 'Concept Breakdown', 'foundation', K.TREE],
  ['learning_objectives', 'Learning Objectives', 'foundation', K.LIST],
  ['prerequisites', 'Prerequisites', 'foundation', K.LIST],
  ['origin_background', 'Origin & Background', 'foundation', K.ORIGIN],
  // Structure
  ['diagram', 'Diagram', 'structure', K.MERMAID],
  ['concept_tree', 'Concept Tree', 'structure', K.TREE],
  ['mind_map', 'Mind Map', 'structure', K.TREE],
  ['knowledge_graph', 'Knowledge Graph', 'structure', K.MERMAID],
  ['dependency_graph', 'Dependency Graph', 'structure', K.MERMAID],
  ['architecture_diagram', 'Architecture Diagram', 'structure', K.MERMAID],
  ['system_diagram', 'System Diagram', 'structure', K.MERMAID],
  // Process
  ['step_by_step_explanation', 'Step-by-Step Explanation', 'process', K.STEPS],
  ['flowchart', 'Flowchart', 'process', K.MERMAID],
  ['workflow_diagram', 'Workflow Diagram', 'process', K.MERMAID],
  ['process_diagram', 'Process Diagram', 'process', K.MERMAID],
  ['sequence_diagram', 'Sequence Diagram', 'process', K.MERMAID],
  ['decision_tree', 'Decision Tree', 'process', K.MERMAID],
  // Temporal
  ['timeline', 'Timeline', 'temporal', K.TIMELINE],
  ['roadmap', 'Roadmap', 'temporal', K.TIMELINE],
  // Spatial
  ['map', 'Map', 'spatial', K.MAP],
  // Data
  ['graph', 'Graph', 'data', K.CHART],
  ['chart', 'Chart', 'data', K.CHART],
  ['dashboard', 'Dashboard', 'data', K.CHART],
  // Comparison
  ['table_comparison', 'Table Comparison', 'comparison', K.TABLE],
  ['decision_matrix', 'Decision Matrix', 'comparison', K.TABLE],
  ['tradeoff_analysis', 'Tradeoff Analysis', 'comparison', K.TABLE],
  // Practical
  ['example', 'Example', 'practical', K.EXAMPLE],
  ['worked_example', 'Worked Example', 'practical', K.EXAMPLE],
  ['use_case', 'Use Case', 'practical', K.EXAMPLE],
  ['case_study', 'Case Study', 'practical', K.EXAMPLE],
  // Code
  ['code_example', 'Code Example', 'code', K.CODE],
  ['code_walkthrough', 'Code Walkthrough', 'code', K.STEPS],
  ['algorithm_walkthrough', 'Algorithm Walkthrough', 'code', K.STEPS],
  ['pseudo_code', 'Pseudo Code', 'code', K.CODE],
  ['dry_run', 'Dry Run', 'code', K.STEPS],
  // Math
  ['formula_explanation', 'Formula Explanation', 'math', K.PROSE],
  ['derivation', 'Derivation', 'math', K.STEPS],
  ['proof', 'Proof', 'math', K.STEPS],
  // Reasoning
  ['reasoning_chain', 'Reasoning Chain', 'reasoning', K.STEPS],
  ['cause_effect_diagram', 'Cause Effect Diagram', 'reasoning', K.MERMAID],
  ['root_cause_analysis', 'Root Cause Analysis', 'reasoning', K.MERMAID],
  // Real world
  ['real_world_analogy', 'Real World Analogy', 'real_world', K.PROSE],
  ['historical_context', 'Historical Context', 'real_world', K.PROSE],
  ['industry_context', 'Industry Context', 'real_world', K.PROSE],
  // Interactive
  ['exercise', 'Exercise', 'interactive', K.QUIZ],
  ['quiz', 'Quiz', 'interactive', K.QUIZ],
  ['simulation', 'Simulation', 'interactive', K.PROSE],
  // Guidance
  ['common_mistakes', 'Common Mistakes', 'guidance', K.LIST],
  ['pitfalls', 'Pitfalls', 'guidance', K.LIST],
  ['anti_patterns', 'Anti Patterns', 'guidance', K.LIST],
  ['best_practices', 'Best Practices', 'guidance', K.LIST],
  // Systems
  ['data_flow_diagram', 'Data Flow Diagram', 'systems', K.MERMAID],
  ['control_flow_diagram', 'Control Flow Diagram', 'systems', K.MERMAID],
  ['event_flow_diagram', 'Event Flow Diagram', 'systems', K.MERMAID],
  // Reinforcement
  ['summary', 'Summary', 'reinforcement', K.PROSE],
  ['cheat_sheet', 'Cheat Sheet', 'reinforcement', K.LIST],
  ['key_takeaways', 'Key Takeaways', 'reinforcement', K.LIST],
  // Internal
  ['topic', 'Topic', 'internal', K.TITLE],
  ['subtopic', 'Subtopic', 'internal', K.TITLE],
  ['supporting_topic', 'Supporting Topic', 'internal', K.TITLE],
  ['problem_statement', 'Problem Statement', 'internal', K.PROSE],
  ['solution_explanation', 'Solution Explanation', 'internal', K.PROSE],
  ['visual_demonstration', 'Visual Demonstration', 'internal', K.IMAGE],
  ['memory_visualization', 'Memory Visualization', 'internal', K.IMAGE],
  ['faq', 'FAQ', 'internal', K.FAQ],
  ['dry_run_overview', 'Dry Run Overview', 'internal', K.PROSE],
  ['dry_run_step', 'Dry Run Step', 'internal', K.STEPS],
  ['dry_run_state', 'Dry Run State', 'internal', K.STATE],
  ['dry_run_summary', 'Dry Run Summary', 'internal', K.PROSE],
  // Legacy aliases (hidden from the editor list)
  ['title', 'Title', 'internal', K.TITLE, true],
  ['hierarchy_tree', 'Hierarchy Tree', 'structure', K.TREE, true],
  ['image', 'Image', 'spatial', K.IMAGE, true],
];

export const ARTIFACTS = DEFS.map(([id, label, category, kind, legacy = false]) => ({
  id,
  label,
  category,
  kind,
  legacy,
}));

export const ARTIFACT_BY_ID = Object.fromEntries(ARTIFACTS.map((a) => [a.id, a]));

export const ARTIFACT_TYPE_IDS = ARTIFACTS.map((a) => a.id);

export const TITLE_TYPE_IDS = new Set(
  ARTIFACTS.filter((a) => a.kind === K.TITLE).map((a) => a.id),
);

/** Grouped, legacy-free options for the plan editor's type dropdown. */
export const ARTIFACT_CATEGORIES = CATEGORY_ORDER.map((category) => ({
  category,
  label: CATEGORY_LABELS[category],
  types: ARTIFACTS.filter((a) => a.category === category && !a.legacy).map((a) => ({
    value: a.id,
    label: a.label,
  })),
})).filter((group) => group.types.length > 0);

function prettifyId(id) {
  return String(id || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function artifactLabel(id) {
  return ARTIFACT_BY_ID[id]?.label || prettifyId(id) || 'Artifact';
}

export function artifactKind(id) {
  return ARTIFACT_BY_ID[id]?.kind || RENDER_KINDS.PROSE;
}

/** Raw generator category id for an artifact (e.g. 'foundation'). */
export function artifactCategory(id) {
  return ARTIFACT_BY_ID[id]?.category || '';
}

/** Human label of the generator category that produces this artifact. */
export function artifactCategoryLabel(id) {
  const category = ARTIFACT_BY_ID[id]?.category;
  return category ? (CATEGORY_LABELS[category] || category) : '';
}

export function isTitleType(id) {
  return TITLE_TYPE_IDS.has(id);
}

/** True when a rendered visual element is a title/topic screen. */
export function isTitleElement(element) {
  if (!element) return false;
  const kind = element.payload?.kind;
  if (kind) return kind === RENDER_KINDS.TITLE;
  return isTitleType(element.visual_type);
}
